import ListItem, { PhoneListItem } from "@/components/ListItem/ListItem";
import ObjectHeading from "@/components/ObjectHeading";
import { Path } from "@/router";
import { Facility } from "@/types";
import { generatePath } from "react-router-dom";
import { useNavigate } from "@/router";
import { renderOperatingStatus } from "@/pages/facilities/_utils";
import { MaterialNames } from "@/components/material_symbol_names";
import SubSectionHeading from "@/components/SubSectionHeading";
import { formattedAddress } from "@/lib/addressHelpers";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";

export interface CustomActionButton {
  text: string;
  variant?: "text" | "outlined" | "filled";
  onClick: () => void;
  testId?: string;
  iconName?: MaterialNames;
}

type Props = {
  facility: Facility | undefined;
  setIsSideSheetOpen: (isOpen: boolean) => void;
  navigate: ReturnType<typeof useNavigate>;
  renderedLabels: {
    operatingStatus: string | undefined;
    facilityType: string | undefined;
    operationType: string | undefined;
  };
  customActionButtons?: CustomActionButton[];
};

function buildFacilityPath(id: string) {
  return generatePath(`/facilities/:id/details/contact`, { id }) as Path;
}

function sectionAffiliationSupportingText(facility: Facility | undefined) {
  return [facility?.section?.name, facility?.chapter?.name].filter(Boolean).join("\n") || undefined;
}

function FacilitySideSheetContent({
  facility,
  renderedLabels,
  setIsSideSheetOpen: _setIsSideSheetOpen,
  navigate,
  customActionButtons
}: Props) {
  const { shouldShowFacilityProfileActions } = useUniversityStaffPermissions();
  const defaultActions = shouldShowFacilityProfileActions
    ? [
        {
          text: "View Facility",
          variant: "outlined" as const,
          onClick: () => {
            if (facility?.id) {
              window.open(buildFacilityPath(facility.id), "_blank");
            }
          }
        }
      ]
    : [];

  return (
    <div>
      <ObjectHeading
        iconName="location_on"
        title={facility?.name!}
        variant="condensed"
        tag={renderOperatingStatus(facility?.operatingStatus, renderedLabels.operatingStatus, "strong")}
        actions={customActionButtons ?? defaultActions}
      />
      <div className="flex flex-col gap-2 items-start mt-8">
        <SubSectionHeading>Details</SubSectionHeading>
        <div className="space-y-4 w-full">
          {(facility?.section || facility?.facilityType) && (
            <ListItem
              iconName="location_on"
              iconBackground
              overline="Facility Type"
              headline={renderedLabels.facilityType}
              supportingText={sectionAffiliationSupportingText(facility)}
            />
          )}
          {facility?.operationType && (
            <ListItem
              iconName="store"
              iconBackground
              overline="Operation Type"
              headline={renderedLabels.operationType}
            />
          )}
        </div>
      </div>

      {(facility?.phone || facility?.fax || facility?.golfShopPhone) && (
        <div className="flex flex-col gap-2 items-start mt-8">
          <SubSectionHeading>Phone</SubSectionHeading>
          <div className="space-y-4 w-full">
            {facility?.phone && <PhoneListItem phone={facility.phone} icon="call" overline="Business" />}
            {facility?.fax && <PhoneListItem phone={facility.fax} icon="fax" overline="Fax" />}
            {facility?.golfShopPhone && (
              <PhoneListItem phone={facility.golfShopPhone} icon="call" overline="Golf Shop" />
            )}
          </div>
        </div>
      )}

      {(facility?.mainAddress || facility?.mailingAddress) && (
        <div className="flex flex-col gap-2 items-start mt-8">
          <SubSectionHeading>Addresses</SubSectionHeading>
          <div className="space-y-4 w-full">
            {facility?.mainAddress && (
              <ListItem
                iconName="location_on"
                iconBackground
                overline="Business Main"
                copyable
                copyText={formattedAddress(facility.mainAddress)}
                headline={
                  <>
                    <span>{facility.mainAddress.address1}</span>
                    <br />
                    <span>{`${facility.mainAddress.city}, ${facility.mainAddress.state} ${facility.mainAddress.postalCode}`}</span>
                  </>
                }
              />
            )}
            {facility?.mailingAddress && (
              <ListItem
                iconName="location_on"
                iconBackground
                overline="Business Mailing"
                copyable
                copyText={formattedAddress(facility.mailingAddress)}
                headline={
                  <>
                    <span>{facility.mailingAddress.address1}</span>
                    <br />
                    <span>{`${facility.mailingAddress.city}, ${facility.mailingAddress.state} ${facility.mailingAddress.postalCode}`}</span>
                  </>
                }
              />
            )}
          </div>
        </div>
      )}

      {facility?.parentCompany && (
        <div className="flex flex-col gap-2 items-start mt-8">
          <SubSectionHeading>Parent Company Details</SubSectionHeading>
          <div className="w-full">
            <ListItem
              iconName="store"
              iconBackground
              overline="Company Name"
              headline={facility.parentCompany.name}
              action={
                shouldShowFacilityProfileActions
                  ? () => {
                      if (facility?.parentCompany?.id) {
                        navigate(
                          generatePath(`/facilities/:facilityId/details/contact`, {
                            facilityId: facility?.parentCompany.id
                          }) as Path
                        );
                      }
                    }
                  : undefined
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default FacilitySideSheetContent;
